import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthContext } from '../context/AuthContext';

/**
 * Real-time listener for the current user's bouts:
 * - myBouts: Bouts user is participating in (active/completed)
 * - openBouts: Bouts user created that are still open (unfilled)
 * - boutInvites: Private bouts where user is invited (open)
 */
export function useMyBouts() {
  const { user } = useAuthContext();
  const [myBouts, setMyBouts] = useState([]);
  const [openBouts, setOpenBouts] = useState([]);
  const [boutInvites, setBoutInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setMyBouts([]);
      setOpenBouts([]);
      setBoutInvites([]);
      setLoading(false);
      return;
    }

    let loaded = 0;
    const checkLoaded = () => {
      if (++loaded >= 3) setLoading(false);
    };

    // Listener 1: Active/completed bouts I'm in
    const q1 = query(
      collection(db, 'bouts'),
      where('participantUids', 'array-contains', user.uid),
      where('status', 'in', ['active', 'completed']),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsub1 = onSnapshot(
      q1,
      (snap) => {
        setMyBouts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        checkLoaded();
      },
      (err) => {
        console.error('Error listening to my bouts:', err);
        checkLoaded();
      }
    );

    // Listener 2: Open bouts I created
    const q2 = query(
      collection(db, 'bouts'),
      where('createdBy', '==', user.uid),
      where('status', '==', 'open'),
      orderBy('createdAt', 'desc')
    );

    const unsub2 = onSnapshot(
      q2,
      (snap) => {
        setOpenBouts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        checkLoaded();
      },
      (err) => {
        console.error('Error listening to open bouts:', err);
        checkLoaded();
      }
    );

    // Listener 3: Private bout invitations for me
    const q3 = query(
      collection(db, 'bouts'),
      where('invitedUid', '==', user.uid),
      where('status', '==', 'open'),
      orderBy('createdAt', 'desc')
    );

    const unsub3 = onSnapshot(
      q3,
      (snap) => {
        setBoutInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        checkLoaded();
      },
      (err) => {
        console.error('Error listening to bout invites:', err);
        checkLoaded();
      }
    );

    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [user]);

  return { myBouts, openBouts, boutInvites, loading };
}
